import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Helper to resolve image URLs for display
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) {
    const baseUrl = API.replace(/\/api$/, '');
    return `${baseUrl}${url}`;
  }
  return `${API}${url.startsWith('/') ? url : '/' + url}`;
};

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  User, 
  Lock, 
  Building2, 
  Globe, 
  Mail, 
  Image,
  Save,
  CheckCircle,
  AlertCircle,
  ScrollText,
  Edit,
  Trash2,
  Plus,
  Download,
  FileJson,
  Monitor,
  Smartphone,
  Clock,
  Camera,
  Coffee,
  CheckSquare,
  LogIn,
  LogOut,
  MapPin,
  Navigation,
  Upload,
  X,
  Shield,
  Calculator,
  Key,
  Timer,
  Play,
  Pause,
  Calendar,
  RefreshCw,
  Settings2,
  Chrome,
  Eye,
  EyeOff,
  Bell,
  Heart,
  MessageCircle
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/i18n/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';
import ThemeToggle from '@/components/ThemeToggle';

function SettingsPage() {
  const { t, isRTL } = useLanguage();
  const [user, setUser] = useState(null);
  const [branding, setBranding] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Personal info form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  
  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Branding form
  const [platformName, setPlatformName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [mobileLogoUrl, setMobileLogoUrl] = useState('');
  const [website, setWebsite] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [uploadingMobileLogo, setUploadingMobileLogo] = useState(false);
  
  // File input refs
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  const mobileLogoInputRef = useRef(null);
  
  // Rules form
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleName, setRuleName] = useState('');
  const [ruleAction, setRuleAction] = useState('');
  const [screenshotRetentionDays, setScreenshotRetentionDays] = useState(0);
  const [screenshotRetentionRuleId, setScreenshotRetentionRuleId] = useState(null);
  const [savingScreenshotRetention, setSavingScreenshotRetention] = useState(false);
  
  // CAPTCHA settings
  const [captchaSettings, setCaptchaSettings] = useState({
    enabled: false,
    captcha_type: 'math',
    google_site_key: '',
    google_secret_key: '',
    hcaptcha_site_key: '',
    hcaptcha_secret_key: '',
    cloudflare_site_key: '',
    cloudflare_secret_key: '',
    enable_for_superadmin: false,
    enable_for_admin: true,
    enable_for_staff: true,
    enable_for_signup: true,
    math_difficulty: 'easy'
  });
  
  // Google OAuth settings
  const [googleOAuthSettings, setGoogleOAuthSettings] = useState({
    enabled: false,
    client_id: '',
    client_secret: '',
    allowed_domains: []
  });
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [googleOAuthSaving, setGoogleOAuthSaving] = useState(false);
  const [newAllowedDomain, setNewAllowedDomain] = useState('');
  
  // Scheduler settings
  const [schedulers, setSchedulers] = useState([]);
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  const [runningScheduler, setRunningScheduler] = useState(null);
  
  useEffect(() => {
    fetchData();
    fetchSchedulers();
    fetchGoogleOAuthSettings();
    fetchUrlSettings();
  }, []);

  // Chat widget settings
  const [chatWidgetEnabled, setChatWidgetEnabled] = useState(false);
  const [chatWidgetSnippet, setChatWidgetSnippet] = useState('');

  // URL settings
  const [urlSettings, setUrlSettings] = useState({
    social_media: [
      { platform: 'facebook', url: '', enabled: false },
      { platform: 'twitter', url: '', enabled: false },
      { platform: 'linkedin', url: '', enabled: false },
      { platform: 'instagram', url: '', enabled: false },
      { platform: 'youtube', url: '', enabled: false },
      { platform: 'tiktok', url: '', enabled: false },
      { platform: 'github', url: '', enabled: false },
      { platform: 'whatsapp', url: '', enabled: false },
    ],
    download_urls: { google_play: '', app_store: '', windows: '', macos: '', linux: '' }
  });
  const [urlSaving, setUrlSaving] = useState(false);

  
  const fetchGoogleOAuthSettings = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/google-oauth-settings`, {
        withCredentials: true
      });
      setGoogleOAuthSettings({
        enabled: response.data.enabled || false,
        client_id: response.data.client_id || '',
        client_secret: response.data.client_secret_masked || '',
        allowed_domains: response.data.allowed_domains || []
      });
    } catch (error) {
      console.error('Failed to fetch Google OAuth settings:', error);
    }
  };

  const fetchUrlSettings = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/urls`, { withCredentials: true });
      setUrlSettings({
        social_media: response.data.social_media || [],
        download_urls: response.data.download_urls || {}
      });
    } catch (error) {
      console.error('Failed to fetch URL settings:', error);
    }
  };

  const handleSaveUrls = async (e) => {
    e.preventDefault();
    setUrlSaving(true);
    try {
      await axios.put(`${API}/superadmin/urls`, urlSettings, { withCredentials: true });
      showMessage('success', 'URL settings saved successfully');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to save URL settings');
    } finally {
      setUrlSaving(false);
    }
  };

  const updateSocialMedia = (index, field, value) => {
    setUrlSettings(prev => {
      const updated = [...prev.social_media];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, social_media: updated };
    });
  };

  const updateDownloadUrl = (key, value) => {
    setUrlSettings(prev => ({
      ...prev,
      download_urls: { ...prev.download_urls, [key]: value }
    }));
  };
  
  const handleSaveGoogleOAuth = async () => {
    setGoogleOAuthSaving(true);
    try {
      await axios.put(
        `${API}/superadmin/google-oauth-settings`,
        {
          enabled: googleOAuthSettings.enabled,
          client_id: googleOAuthSettings.client_id,
          client_secret: googleOAuthSettings.client_secret,
          allowed_domains: googleOAuthSettings.allowed_domains
        },
        { withCredentials: true }
      );
      showMessage('success', 'Google OAuth settings saved successfully');
      // Refresh to get masked secret
      fetchGoogleOAuthSettings();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to save Google OAuth settings');
    } finally {
      setGoogleOAuthSaving(false);
    }
  };
  
  const addAllowedDomain = () => {
    if (newAllowedDomain && !googleOAuthSettings.allowed_domains.includes(newAllowedDomain)) {
      setGoogleOAuthSettings({
        ...googleOAuthSettings,
        allowed_domains: [...googleOAuthSettings.allowed_domains, newAllowedDomain.toLowerCase().trim()]
      });
      setNewAllowedDomain('');
    }
  };
  
  const removeAllowedDomain = (domain) => {
    setGoogleOAuthSettings({
      ...googleOAuthSettings,
      allowed_domains: googleOAuthSettings.allowed_domains.filter(d => d !== domain)
    });
  };
  
  const fetchData = async () => {
    try {
      const [userRes, brandingRes, rulesRes, captchaRes, chatRes] = await Promise.all([
        axios.get(`${API}/superadmin/settings`, { withCredentials: true }),
        axios.get(`${API}/superadmin/platform-branding`, { withCredentials: true }),
        axios.get(`${API}/superadmin/rules`, { withCredentials: true }),
        axios.get(`${API}/superadmin/captcha-settings`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/superadmin/chat-widget`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      
      setUser(userRes.data);
      setName(userRes.data.name || '');
      setEmail(userRes.data.email || '');
      
      setBranding(brandingRes.data);
      setPlatformName(brandingRes.data.platform_name || 'Trackly');
      setLogoUrl(brandingRes.data.logo_url || '');
      setFaviconUrl(brandingRes.data.favicon_url || '');
      setMobileLogoUrl(brandingRes.data.mobile_logo_url || '');
      setWebsite(brandingRes.data.website || '');
      setContactEmail(brandingRes.data.contact_email || '');
      
      setRules(rulesRes.data || []);
      
      // Extract screenshot_retention_days platform rule
      const screenshotRule = (rulesRes.data || []).find(r => r.rule_name === 'screenshot_retention_days');
      if (screenshotRule) {
        setScreenshotRetentionDays(parseInt(screenshotRule.rule_action) || 0);
        setScreenshotRetentionRuleId(screenshotRule.rule_id);
      } else {
        setScreenshotRetentionDays(0);
        setScreenshotRetentionRuleId(null);
      }
      
      if (captchaRes.data) {
        setCaptchaSettings({
          enabled: captchaRes.data.enabled || false,
          captcha_type: captchaRes.data.captcha_type || 'math',
          google_site_key: captchaRes.data.google_site_key || '',
          google_secret_key: captchaRes.data.google_secret_key || '',
          hcaptcha_site_key: captchaRes.data.hcaptcha_site_key || '',
          hcaptcha_secret_key: captchaRes.data.hcaptcha_secret_key || '',
          cloudflare_site_key: captchaRes.data.cloudflare_site_key || '',
          cloudflare_secret_key: captchaRes.data.cloudflare_secret_key || '',
          enable_for_superadmin: captchaRes.data.enable_for_superadmin || false,
          enable_for_admin: captchaRes.data.enable_for_admin !== false,
          enable_for_staff: captchaRes.data.enable_for_staff !== false,
          enable_for_signup: captchaRes.data.enable_for_signup !== false,
          math_difficulty: captchaRes.data.math_difficulty || 'easy'
        });
      }

      if (chatRes.data) {
        setChatWidgetEnabled(chatRes.data.enabled || false);
        setChatWidgetSnippet(chatRes.data.snippet || '');
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSchedulers = async () => {
    setSchedulerLoading(true);
    try {
      const response = await axios.get(`${API}/superadmin/schedulers`, {
        withCredentials: true
      });
      setSchedulers(response.data.schedulers || []);
    } catch (error) {
      console.error('Failed to fetch schedulers:', error);
    } finally {
      setSchedulerLoading(false);
    }
  };
  
  const handleUpdateScheduler = async (schedulerId, updates) => {
    try {
      await axios.put(
        `${API}/superadmin/schedulers/${schedulerId}`,
        { scheduler_id: schedulerId, ...updates },
        { withCredentials: true }
      );
      showMessage('success', 'Scheduler updated successfully');
      fetchSchedulers();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to update scheduler');
    }
  };
  
  const handleRunScheduler = async (schedulerId) => {
    setRunningScheduler(schedulerId);
    try {
      const response = await axios.post(
        `${API}/superadmin/schedulers/${schedulerId}/run`,
        {},
        { withCredentials: true }
      );
      showMessage('success', response.data.message || 'Scheduler executed successfully');
      fetchSchedulers();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to run scheduler');
    } finally {
      setRunningScheduler(null);
    }
  };
  
  const formatNextRun = (isoString) => {
    if (!isoString) return 'Not scheduled';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = date - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffHours < 24) {
      return `In ${diffHours}h (${date.toLocaleTimeString()})`;
    }
    return `In ${diffDays}d (${date.toLocaleDateString()})`;
  };
  
  const getCategoryColor = (category) => {
    const colors = {
      'Attendance': 'bg-blue-100 text-blue-700',
      'Billing': 'bg-green-100 text-green-700',
      'System': 'bg-purple-100 text-purple-700',
      'Notifications': 'bg-amber-100 text-amber-700',
      'Reports': 'bg-indigo-100 text-indigo-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };
  
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };
  
  const handleSavePersonalInfo = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(
        `${API}/superadmin/settings`,
        { name, email },
        { withCredentials: true }
      );
      showMessage('success', 'Personal information updated successfully');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to update personal information');
    } finally {
      setSaving(false);
    }
  };
  
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      showMessage('error', 'New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      showMessage('error', 'Password must be at least 6 characters');
      return;
    }
    
    setSaving(true);
    
    try {
      await axios.post(
        `${API}/superadmin/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true }
      );
      showMessage('success', 'Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveBranding = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(
        `${API}/superadmin/platform-branding`,
        {
          platform_name: platformName,
          logo_url: logoUrl || null,
          favicon_url: faviconUrl || null,
          mobile_logo_url: mobileLogoUrl || null,
          website: website || null,
          contact_email: contactEmail || null
        },
        { withCredentials: true }
      );
      showMessage('success', 'Platform branding updated successfully');
      
      // Update favicon if changed
      if (faviconUrl) {
        const favicon = document.querySelector('link[rel="icon"]');
        if (favicon) {
          favicon.href = faviconUrl;
        }
      }
      
      // Clear localStorage to force refresh of branding
      localStorage.removeItem('platformBranding');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to update branding');
    } finally {
      setSaving(false);
    }
  };
  
  const handleFileUpload = async (file, imageType, setUrl, setUploading) => {
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/superadmin/upload-branding-image?image_type=${imageType}`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      setUrl(response.data.url);
      showMessage('success', response.data.message);
    } catch (error) {
      showMessage('error', error.response?.data?.detail || `Failed to upload ${imageType}`);
    } finally {
      setUploading(false);
    }
  };
  
  const handleClearImage = (setUrl, inputRef) => {
    setUrl('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };
  
  const handleSaveScreenshotRetention = async (e) => {
    e.preventDefault();
    setSavingScreenshotRetention(true);
    try {
      if (screenshotRetentionRuleId) {
        // Update existing rule
        await axios.put(
          `${API}/superadmin/rules/${screenshotRetentionRuleId}`,
          { rule_name: 'screenshot_retention_days', rule_action: String(screenshotRetentionDays) },
          { withCredentials: true }
        );
      } else {
        // Create new rule
        const res = await axios.post(
          `${API}/superadmin/rules`,
          { rule_name: 'screenshot_retention_days', rule_action: String(screenshotRetentionDays) },
          { withCredentials: true }
        );
        setScreenshotRetentionRuleId(res.data?.rule_id || null);
      }
      showMessage('success', screenshotRetentionDays > 0
        ? `Screenshot auto-deletion set to ${screenshotRetentionDays} day(s)`
        : 'Screenshot auto-deletion disabled');
      fetchData();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to save screenshot retention setting');
    } finally {
      setSavingScreenshotRetention(false);
    }
  };

  const openRuleDialog = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setRuleName(rule.rule_name);
      setRuleAction(rule.rule_action);
    } else {
      setEditingRule(null);
      setRuleName('');
      setRuleAction('');
    }
    setRuleDialogOpen(true);
  };
  
  const handleSaveRule = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editingRule) {
        await axios.put(
          `${API}/superadmin/rules/${editingRule.rule_id}`,
          { rule_name: ruleName, rule_action: ruleAction },
          { withCredentials: true }
        );
        showMessage('success', 'Rule updated successfully');
      } else {
        await axios.post(
          `${API}/superadmin/rules`,
          { rule_name: ruleName, rule_action: ruleAction },
          { withCredentials: true }
        );
        showMessage('success', 'Rule created successfully');
      }
      
      setRuleDialogOpen(false);
      fetchData();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      await axios.delete(`${API}/superadmin/rules/${ruleId}`, { withCredentials: true });
      showMessage('success', 'Rule deleted successfully');
      fetchData();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to delete rule');
    }
  };

  const handleSaveChatWidget = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await axios.put(
        `${API}/superadmin/chat-widget`,
        { enabled: chatWidgetEnabled, snippet: chatWidgetSnippet },
        { withCredentials: true }
      );
      showMessage('success', 'Chat widget settings saved successfully');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to save chat widget settings');
    } finally {
      setSaving(false);
    }
  };

  
  const handleSaveCaptcha = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(
        `${API}/superadmin/captcha-settings`,
        captchaSettings,
        { withCredentials: true }
      );
      showMessage('success', 'CAPTCHA settings updated successfully');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to update CAPTCHA settings');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900">{t("settings.title")}</h2>
        <p className="text-gray-600 mt-1">Manage your account and platform settings</p>
      </div>
      
      {/* Status Message */}
      {message.text && (
        <div className={`
          flex items-center gap-2 p-4 rounded-lg
          ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : ''}
          ${message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : ''}
        `}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}
      
      <Tabs defaultValue="personal" className="w-full">
        <TabsList className="grid w-full grid-cols-11">
          <TabsTrigger value="personal" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            {t('settings.personal_info')}
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            {t('settings.security')}
          </TabsTrigger>
          <TabsTrigger value="branding" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {t('settings.branding')}
          </TabsTrigger>
          <TabsTrigger value="urls" className="flex items-center gap-2" data-testid="urls-tab">
            <Globe className="w-4 h-4" />
            {t('settings.urls')}
          </TabsTrigger>
          <TabsTrigger value="google-oauth" className="flex items-center gap-2" data-testid="google-oauth-tab">
            <Chrome className="w-4 h-4" />
            {t('settings.google_login')}
          </TabsTrigger>
          <TabsTrigger value="captcha" className="flex items-center gap-2" data-testid="captcha-tab">
            <Shield className="w-4 h-4" />
            {t('settings.captcha')}
          </TabsTrigger>
          <TabsTrigger value="schedulers" className="flex items-center gap-2" data-testid="schedulers-tab">
            <Timer className="w-4 h-4" />
            {t('settings.schedulers')}
          </TabsTrigger>
          <TabsTrigger value="rules" className="flex items-center gap-2" data-testid="rules-tab">
            <ScrollText className="w-4 h-4" />
            {t('settings.rules')}
          </TabsTrigger>
          <TabsTrigger value="postman" className="flex items-center gap-2" data-testid="postman-tab">
            <FileJson className="w-4 h-4" />
            {t('settings.postman')}
          </TabsTrigger>
          <TabsTrigger value="chat-widget" className="flex items-center gap-2" data-testid="chat-widget-tab">
            <MessageCircle className="w-4 h-4" />
            {t('settings.chat_widget')}
          </TabsTrigger>
          <TabsTrigger value="language" className="flex items-center gap-2" data-testid="language-tab">
            <Globe className="w-4 h-4" />
            {t('common.language')}
          </TabsTrigger>
        </TabsList>
        
        {/* Personal Info Tab */}
        <TabsContent value="personal">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-indigo-600" />
                Personal Information
              </CardTitle>
              <CardDescription>Update your personal details</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSavePersonalInfo} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required
                    data-testid="name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="email-input"
                  />
                </div>
                <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Security Tab */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-indigo-600" />
                Change Password
              </CardTitle>
              <CardDescription>Update your account password</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    data-testid="current-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={6}
                    required
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={6}
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                  <Lock className="w-4 h-4 mr-2" />
                  {saving ? 'Changing...' : 'Change Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Platform Branding Tab */}
        <TabsContent value="branding">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-indigo-600" />
                Platform Branding
              </CardTitle>
              <CardDescription>
                Customize your platform appearance. These settings will be displayed across all login pages and headers.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveBranding} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="platform-name" className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Platform Name
                      </Label>
                      <Input
                        id="platform-name"
                        value={platformName}
                        onChange={(e) => setPlatformName(e.target.value)}
                        placeholder="Trackly"
                        required
                        data-testid="platform-name-input"
                      />
                      <p className="text-xs text-gray-500">Displayed in headers and titles</p>
                    </div>
                    
                    {/* Upload Logo */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        Upload Logo
                      </Label>
                      <div className="flex items-center gap-2">
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                          onChange={(e) => handleFileUpload(e.target.files[0], 'logo', setLogoUrl, setUploadingLogo)}
                          className="hidden"
                          data-testid="logo-file-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={uploadingLogo}
                          className="flex-1"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingLogo ? 'Uploading...' : 'Choose File'}
                        </Button>
                        {logoUrl && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleClearImage(setLogoUrl, logoInputRef)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      {logoUrl && (
                        <div className="mt-2 p-2 bg-gray-50 rounded border">
                          <img src={resolveImageUrl(logoUrl)} alt="Logo Preview" className="h-12 object-contain" />
                        </div>
                      )}
                      <p className="text-xs text-gray-500">Recommended size: 200x50px (PNG, JPG, WebP, SVG)</p>
                    </div>
                    
                    {/* Upload Favicon */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        Upload Favicon
                      </Label>
                      <div className="flex items-center gap-2">
                        <input
                          ref={faviconInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/ico,image/x-icon,image/webp"
                          onChange={(e) => handleFileUpload(e.target.files[0], 'favicon', setFaviconUrl, setUploadingFavicon)}
                          className="hidden"
                          data-testid="favicon-file-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => faviconInputRef.current?.click()}
                          disabled={uploadingFavicon}
                          className="flex-1"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingFavicon ? 'Uploading...' : 'Choose File'}
                        </Button>
                        {faviconUrl && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleClearImage(setFaviconUrl, faviconInputRef)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      {faviconUrl && (
                        <div className="mt-2 p-2 bg-gray-50 rounded border flex items-center gap-2">
                          <img src={resolveImageUrl(faviconUrl)} alt="Favicon Preview" className="h-8 w-8 object-contain" />
                          <span className="text-xs text-gray-500">Favicon uploaded</span>
                        </div>
                      )}
                      <p className="text-xs text-gray-500">Recommended size: 32x32px (ICO, PNG, JPG)</p>
                    </div>
                    
                    {/* Upload Mobile Logo */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Smartphone className="w-4 h-4" />
                        Upload Mobile Logo
                      </Label>
                      <div className="flex items-center gap-2">
                        <input
                          ref={mobileLogoInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          onChange={(e) => handleFileUpload(e.target.files[0], 'mobile_logo', setMobileLogoUrl, setUploadingMobileLogo)}
                          className="hidden"
                          data-testid="mobile-logo-file-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => mobileLogoInputRef.current?.click()}
                          disabled={uploadingMobileLogo}
                          className="flex-1"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingMobileLogo ? 'Uploading...' : 'Choose File'}
                        </Button>
                        {mobileLogoUrl && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleClearImage(setMobileLogoUrl, mobileLogoInputRef)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      {mobileLogoUrl && (
                        <div className="mt-2 p-2 bg-gray-50 rounded border">
                          <img src={resolveImageUrl(mobileLogoUrl)} alt="Mobile Logo Preview" className="h-12 object-contain" />
                        </div>
                      )}
                      <p className="text-xs text-gray-500">Used for Android &quot;Add to Home Screen&quot; icon. Recommended: 512x512px square (PNG)</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="website" className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Website URL
                      </Label>
                      <Input
                        id="website"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        placeholder="https://www.yourcompany.com"
                        data-testid="website-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="contact-email" className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Contact Email
                      </Label>
                      <Input
                        id="contact-email"
                        type="email"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        placeholder="support@yourcompany.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                    
                    {/* Preview */}
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                      <p className="text-xs text-gray-500 mb-2">Logo Preview</p>
                      <div className="flex items-center gap-3">
                        {logoUrl ? (
                          <img src={resolveImageUrl(logoUrl)} alt="Logo" className="h-8 object-contain" onError={(e) => e.target.style.display = 'none'} />
                        ) : (
                          <div className="w-8 h-8 bg-indigo-100 rounded flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-indigo-600" />
                          </div>
                        )}
                        <span className="font-bold text-gray-900">{platformName || 'Trackly'}</span>
                      </div>
                    </div>
                    
                    {/* Mobile Icon Preview */}
                    {mobileLogoUrl && (
                      <div className="p-4 bg-gray-50 rounded-lg border">
                        <p className="text-xs text-gray-500 mb-2">Mobile App Icon Preview</p>
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 rounded-xl overflow-hidden shadow-md bg-white">
                            <img src={resolveImageUrl(mobileLogoUrl)} alt="Mobile Icon" className="w-full h-full object-cover" />
                          </div>
                          <span className="text-sm text-gray-600">Home screen icon</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Branding'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* URLs Tab */}
        <TabsContent value="urls">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-600" />
                {t('settings.url_management')}
              </CardTitle>
              <CardDescription>{t('settings.url_management_desc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveUrls} className="space-y-8">
                {/* Social Media URLs */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Heart className="w-5 h-5 text-pink-500" />
                    {t('settings.social_media_urls')}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">{t('settings.social_media_urls_desc')}</p>
                  <div className="space-y-3">
                    {urlSettings.social_media.map((item, index) => (
                      <div key={item.platform} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border" data-testid={`social-media-${item.platform}`}>
                        <Switch
                          checked={item.enabled}
                          onCheckedChange={(checked) => updateSocialMedia(index, 'enabled', checked)}
                          data-testid={`social-toggle-${item.platform}`}
                        />
                        <span className="w-24 text-sm font-medium capitalize text-gray-700">{item.platform}</span>
                        <Input
                          value={item.url}
                          onChange={(e) => updateSocialMedia(index, 'url', e.target.value)}
                          placeholder={`https://${item.platform}.com/yourpage`}
                          className="flex-1"
                          data-testid={`social-url-${item.platform}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Download URLs */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Download className="w-5 h-5 text-blue-500" />
                    {t('settings.download_urls')}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">{t('settings.download_urls_desc')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Smartphone className="w-4 h-4" />
                        Google Play Store
                      </Label>
                      <Input
                        value={urlSettings.download_urls.google_play || ''}
                        onChange={(e) => updateDownloadUrl('google_play', e.target.value)}
                        placeholder="https://play.google.com/store/apps/details?id=..."
                        data-testid="download-url-google-play"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Smartphone className="w-4 h-4" />
                        Apple App Store
                      </Label>
                      <Input
                        value={urlSettings.download_urls.app_store || ''}
                        onChange={(e) => updateDownloadUrl('app_store', e.target.value)}
                        placeholder="https://apps.apple.com/app/..."
                        data-testid="download-url-app-store"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Monitor className="w-4 h-4" />
                        Windows
                      </Label>
                      <Input
                        value={urlSettings.download_urls.windows || ''}
                        onChange={(e) => updateDownloadUrl('windows', e.target.value)}
                        placeholder="https://example.com/download/windows"
                        data-testid="download-url-windows"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Monitor className="w-4 h-4" />
                        macOS
                      </Label>
                      <Input
                        value={urlSettings.download_urls.macos || ''}
                        onChange={(e) => updateDownloadUrl('macos', e.target.value)}
                        placeholder="https://example.com/download/macos"
                        data-testid="download-url-macos"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Monitor className="w-4 h-4" />
                        Linux / Ubuntu
                      </Label>
                      <Input
                        value={urlSettings.download_urls.linux || ''}
                        onChange={(e) => updateDownloadUrl('linux', e.target.value)}
                        placeholder="https://example.com/download/linux"
                        data-testid="download-url-linux"
                      />
                    </div>
                  </div>
                </div>

                <Button type="submit" disabled={urlSaving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-urls-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {urlSaving ? 'Saving...' : t('settings.save_settings')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Google OAuth Tab */}
        <TabsContent value="google-oauth">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Chrome className="w-5 h-5 text-indigo-600" />
                Google Login Configuration
              </CardTitle>
              <CardDescription>
                Configure your own Google OAuth credentials for Company Admin login. 
                This allows you to use your own Google Cloud project for authentication instead of the default.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Enable Custom Google OAuth */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                  <div className="space-y-1">
                    <Label className="text-base font-medium">Enable Custom Google OAuth</Label>
                    <p className="text-sm text-gray-600">
                      Use your own Google OAuth credentials for admin login
                    </p>
                  </div>
                  <Switch
                    checked={googleOAuthSettings.enabled}
                    onCheckedChange={(checked) => setGoogleOAuthSettings(prev => ({ ...prev, enabled: checked }))}
                    data-testid="google-oauth-enabled-switch"
                  />
                </div>

                {googleOAuthSettings.enabled && (
                  <>
                    {/* OAuth Credentials */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-medium text-blue-900 mb-4 flex items-center gap-2">
                        <Key className="w-4 h-4" />
                        Google OAuth Credentials
                      </h4>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="google-client-id">Client ID</Label>
                          <Input
                            id="google-client-id"
                            value={googleOAuthSettings.client_id}
                            onChange={(e) => setGoogleOAuthSettings(prev => ({ ...prev, client_id: e.target.value }))}
                            placeholder="123456789-abc123def456.apps.googleusercontent.com"
                            data-testid="google-client-id-input"
                          />
                          <p className="text-xs text-gray-500">Your Google OAuth 2.0 Client ID</p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="google-client-secret">Client Secret</Label>
                          <div className="relative">
                            <Input
                              id="google-client-secret"
                              type={showClientSecret ? "text" : "password"}
                              value={googleOAuthSettings.client_secret}
                              onChange={(e) => setGoogleOAuthSettings(prev => ({ ...prev, client_secret: e.target.value }))}
                              placeholder="GOCSPX-xxxxxxxxxxxxxxxxxxxxxxxx"
                              data-testid="google-client-secret-input"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-1 top-1/2 -translate-y-1/2"
                              onClick={() => setShowClientSecret(!showClientSecret)}
                            >
                              {showClientSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                          </div>
                          <p className="text-xs text-gray-500">Your Google OAuth 2.0 Client Secret</p>
                        </div>
                      </div>
                    </div>

                    {/* Allowed Domains */}
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <h4 className="font-medium text-amber-900 mb-4 flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Allowed Email Domains (Optional)
                      </h4>
                      <p className="text-sm text-amber-700 mb-4">
                        Restrict login to specific email domains. Leave empty to allow all domains.
                      </p>
                      <div className="flex gap-2 mb-4">
                        <Input
                          value={newAllowedDomain}
                          onChange={(e) => setNewAllowedDomain(e.target.value)}
                          placeholder="company.com"
                          className="max-w-xs"
                          data-testid="allowed-domain-input"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addAllowedDomain();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={addAllowedDomain}
                          data-testid="add-domain-btn"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add
                        </Button>
                      </div>
                      {googleOAuthSettings.allowed_domains.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {googleOAuthSettings.allowed_domains.map((domain) => (
                            <span
                              key={domain}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-white border border-amber-300 rounded-full text-sm"
                            >
                              @{domain}
                              <button
                                type="button"
                                onClick={() => removeAllowedDomain(domain)}
                                className="text-amber-600 hover:text-amber-800"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Setup Instructions */}
                    <div className="p-4 bg-gray-50 rounded-lg border">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Setup Instructions
                      </h4>
                      <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                        <li>Go to the <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Google Cloud Console</a></li>
                        <li>Create a new OAuth 2.0 Client ID (Web application)</li>
                        <li>Add your application URL to &quot;Authorized JavaScript origins&quot;</li>
                        <li>Add your callback URL to &quot;Authorized redirect URIs&quot;</li>
                        <li>Copy the Client ID and Client Secret above</li>
                      </ol>
                    </div>
                  </>
                )}

                <Button
                  onClick={handleSaveGoogleOAuth}
                  disabled={googleOAuthSaving}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-google-oauth-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {googleOAuthSaving ? 'Saving...' : 'Save Google OAuth Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* CAPTCHA Tab */}
        <TabsContent value="captcha">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-indigo-600" />
                CAPTCHA Settings
              </CardTitle>
              <CardDescription>
                Configure CAPTCHA protection for login forms to prevent automated attacks
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveCaptcha} className="space-y-6">
                {/* Enable CAPTCHA */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                  <div className="space-y-1">
                    <Label className="text-base font-medium">Enable CAPTCHA Protection</Label>
                    <p className="text-sm text-gray-600">
                      Require CAPTCHA verification for all login attempts
                    </p>
                  </div>
                  <Switch
                    checked={captchaSettings.enabled}
                    onCheckedChange={(checked) => setCaptchaSettings(prev => ({ ...prev, enabled: checked }))}
                    data-testid="captcha-enabled-switch"
                  />
                </div>

                {captchaSettings.enabled && (
                  <>
                    {/* CAPTCHA Type Selection */}
                    <div className="space-y-3">
                      <Label className="text-base font-medium">CAPTCHA Type</Label>
                      <Select
                        value={captchaSettings.captcha_type}
                        onValueChange={(value) => setCaptchaSettings(prev => ({ ...prev, captcha_type: value }))}
                      >
                        <SelectTrigger data-testid="captcha-type-select">
                          <SelectValue placeholder="Select CAPTCHA type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="math">
                            <div className="flex items-center gap-2">
                              <Calculator className="w-4 h-4" />
                              Math CAPTCHA (Simple arithmetic)
                            </div>
                          </SelectItem>
                          <SelectItem value="recaptcha">
                            <div className="flex items-center gap-2">
                              <Shield className="w-4 h-4" />
                              Google reCAPTCHA v2
                            </div>
                          </SelectItem>
                          <SelectItem value="hcaptcha">
                            <div className="flex items-center gap-2">
                              <Shield className="w-4 h-4" />
                              hCaptcha
                            </div>
                          </SelectItem>
                          <SelectItem value="turnstile">
                            <div className="flex items-center gap-2">
                              <Shield className="w-4 h-4" />
                              Cloudflare Turnstile
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Math CAPTCHA Settings */}
                    {captchaSettings.captcha_type === 'math' && (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
                          <Calculator className="w-4 h-4" />
                          Math CAPTCHA Settings
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <Label>Difficulty Level</Label>
                            <Select
                              value={captchaSettings.math_difficulty}
                              onValueChange={(value) => setCaptchaSettings(prev => ({ ...prev, math_difficulty: value }))}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="easy">Easy (1-10)</SelectItem>
                                <SelectItem value="medium">Medium (1-50)</SelectItem>
                                <SelectItem value="hard">Hard (1-100)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <p className="text-sm text-blue-700">
                            Math CAPTCHA generates simple arithmetic problems like &quot;5 + 3 = ?&quot; for users to solve.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Google reCAPTCHA Settings */}
                    {captchaSettings.captcha_type === 'recaptcha' && (
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <h4 className="font-medium text-green-900 mb-3 flex items-center gap-2">
                          <Key className="w-4 h-4" />
                          Google reCAPTCHA v2 Configuration
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="google-site-key">Site Key</Label>
                            <Input
                              id="google-site-key"
                              value={captchaSettings.google_site_key}
                              onChange={(e) => setCaptchaSettings(prev => ({ ...prev, google_site_key: e.target.value }))}
                              placeholder="6Lc..."
                              data-testid="google-site-key-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="google-secret-key">Secret Key</Label>
                            <Input
                              id="google-secret-key"
                              type="password"
                              value={captchaSettings.google_secret_key}
                              onChange={(e) => setCaptchaSettings(prev => ({ ...prev, google_secret_key: e.target.value }))}
                              placeholder="6Lc..."
                              data-testid="google-secret-key-input"
                            />
                          </div>
                          <p className="text-sm text-green-700">
                            Get your keys from <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noopener noreferrer" className="underline">Google reCAPTCHA Admin Console</a>
                          </p>
                        </div>
                      </div>
                    )}

                    {/* hCaptcha Settings */}
                    {captchaSettings.captcha_type === 'hcaptcha' && (
                      <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <h4 className="font-medium text-purple-900 mb-3 flex items-center gap-2">
                          <Key className="w-4 h-4" />
                          hCaptcha Configuration
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="hcaptcha-site-key">Site Key</Label>
                            <Input
                              id="hcaptcha-site-key"
                              value={captchaSettings.hcaptcha_site_key}
                              onChange={(e) => setCaptchaSettings(prev => ({ ...prev, hcaptcha_site_key: e.target.value }))}
                              placeholder="10000000-ffff-ffff-ffff-000000000001"
                              data-testid="hcaptcha-site-key-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="hcaptcha-secret-key">Secret Key</Label>
                            <Input
                              id="hcaptcha-secret-key"
                              type="password"
                              value={captchaSettings.hcaptcha_secret_key}
                              onChange={(e) => setCaptchaSettings(prev => ({ ...prev, hcaptcha_secret_key: e.target.value }))}
                              placeholder="0x0000000000000000000000000000000000000000"
                              data-testid="hcaptcha-secret-key-input"
                            />
                          </div>
                          <p className="text-sm text-purple-700">
                            Get your keys from <a href="https://dashboard.hcaptcha.com/sites" target="_blank" rel="noopener noreferrer" className="underline">hCaptcha Dashboard</a>
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Cloudflare Turnstile Settings */}
                    {captchaSettings.captcha_type === 'turnstile' && (
                      <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <h4 className="font-medium text-orange-900 mb-3 flex items-center gap-2">
                          <Key className="w-4 h-4" />
                          Cloudflare Turnstile Configuration
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="cloudflare-site-key">Site Key</Label>
                            <Input
                              id="cloudflare-site-key"
                              value={captchaSettings.cloudflare_site_key}
                              onChange={(e) => setCaptchaSettings(prev => ({ ...prev, cloudflare_site_key: e.target.value }))}
                              placeholder="0x4AAAAAAABkMYinukE_cwtaH"
                              data-testid="cloudflare-site-key-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="cloudflare-secret-key">Secret Key</Label>
                            <Input
                              id="cloudflare-secret-key"
                              type="password"
                              value={captchaSettings.cloudflare_secret_key}
                              onChange={(e) => setCaptchaSettings(prev => ({ ...prev, cloudflare_secret_key: e.target.value }))}
                              placeholder="0x4AAAAAAABkMYinukE_cwtaH"
                              data-testid="cloudflare-secret-key-input"
                            />
                          </div>
                          <p className="text-sm text-orange-700">
                            Get your keys from <a href="https://dash.cloudflare.com/?to=/:account/turnstile" target="_blank" rel="noopener noreferrer" className="underline">Cloudflare Dashboard</a>
                          </p>
                        </div>
                      </div>
                    )}

                    {/* User Role Settings */}
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Enable CAPTCHA for User Roles</h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <Label className="font-medium">Super Admin</Label>
                            <p className="text-sm text-gray-600">Require CAPTCHA for super admin login</p>
                          </div>
                          <Switch
                            checked={captchaSettings.enable_for_superadmin}
                            onCheckedChange={(checked) => setCaptchaSettings(prev => ({ ...prev, enable_for_superadmin: checked }))}
                            data-testid="captcha-superadmin-switch"
                          />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <Label className="font-medium">Admin</Label>
                            <p className="text-sm text-gray-600">Require CAPTCHA for admin login</p>
                          </div>
                          <Switch
                            checked={captchaSettings.enable_for_admin}
                            onCheckedChange={(checked) => setCaptchaSettings(prev => ({ ...prev, enable_for_admin: checked }))}
                            data-testid="captcha-admin-switch"
                          />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <Label className="font-medium">Staff</Label>
                            <p className="text-sm text-gray-600">Require CAPTCHA for staff login</p>
                          </div>
                          <Switch
                            checked={captchaSettings.enable_for_staff}
                            onCheckedChange={(checked) => setCaptchaSettings(prev => ({ ...prev, enable_for_staff: checked }))}
                            data-testid="captcha-staff-switch"
                          />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <Label className="font-medium">Company Signup</Label>
                            <p className="text-sm text-gray-600">Require CAPTCHA on /admin/signup (including referral links)</p>
                          </div>
                          <Switch
                            checked={captchaSettings.enable_for_signup}
                            onCheckedChange={(checked) => setCaptchaSettings(prev => ({ ...prev, enable_for_signup: checked }))}
                            data-testid="captcha-signup-switch"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save CAPTCHA Settings'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Schedulers Tab */}
        <TabsContent value="schedulers">
          <Card>
            <CardHeader className="border-b bg-gray-50">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Timer className="w-5 h-5 text-indigo-600" />
                    Scheduled Tasks
                  </CardTitle>
                  <CardDescription>Manage automated background tasks and their schedules</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchSchedulers}
                  disabled={schedulerLoading}
                  data-testid="refresh-schedulers-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${schedulerLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {schedulerLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Task Name</TableHead>
                      <TableHead className="font-semibold">Category</TableHead>
                      <TableHead className="font-semibold">Frequency</TableHead>
                      <TableHead className="font-semibold">Run Time</TableHead>
                      <TableHead className="font-semibold">Run Day</TableHead>
                      <TableHead className="font-semibold">Next Run</TableHead>
                      <TableHead className="font-semibold">{t("common.status")}</TableHead>
                      <TableHead className="font-semibold text-right">{t("common.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schedulers.map((scheduler) => (
                      <TableRow key={scheduler.scheduler_id} className="hover:bg-gray-50">
                        <TableCell>
                          <div>
                            <p className="font-medium text-gray-900">{scheduler.name}</p>
                            <p className="text-xs text-gray-500 max-w-xs">{scheduler.description}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(scheduler.category)}`}>
                            {scheduler.category}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={scheduler.frequency}
                            onValueChange={(value) => handleUpdateScheduler(scheduler.scheduler_id, { frequency: value })}
                          >
                            <SelectTrigger className="w-24 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="daily">Daily</SelectItem>
                              <SelectItem value="weekly">Weekly</SelectItem>
                              <SelectItem value="monthly">Monthly</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="time"
                            value={scheduler.run_time}
                            onChange={(e) => handleUpdateScheduler(scheduler.scheduler_id, { run_time: e.target.value })}
                            className="w-28 h-8"
                            data-testid={`scheduler-time-${scheduler.scheduler_id}`}
                          />
                        </TableCell>
                        <TableCell>
                          {scheduler.frequency === 'weekly' ? (
                            <Select
                              value={String(scheduler.run_day || 0)}
                              onValueChange={(value) => handleUpdateScheduler(scheduler.scheduler_id, { run_day: parseInt(value) })}
                            >
                              <SelectTrigger className="w-28 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">Sunday</SelectItem>
                                <SelectItem value="1">Monday</SelectItem>
                                <SelectItem value="2">Tuesday</SelectItem>
                                <SelectItem value="3">Wednesday</SelectItem>
                                <SelectItem value="4">Thursday</SelectItem>
                                <SelectItem value="5">Friday</SelectItem>
                                <SelectItem value="6">Saturday</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : scheduler.frequency === 'monthly' ? (
                            <Input
                              type="number"
                              min="1"
                              max="31"
                              value={scheduler.run_day || 1}
                              onChange={(e) => handleUpdateScheduler(scheduler.scheduler_id, { run_day: parseInt(e.target.value) })}
                              className="w-20 h-8"
                              data-testid={`scheduler-day-${scheduler.scheduler_id}`}
                            />
                          ) : (
                            <span className="text-gray-400 text-sm">N/A</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3 text-gray-400" />
                            <span className="text-sm text-gray-600">{formatNextRun(scheduler.next_run)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={scheduler.enabled}
                            onCheckedChange={(checked) => handleUpdateScheduler(scheduler.scheduler_id, { enabled: checked })}
                            data-testid={`scheduler-toggle-${scheduler.scheduler_id}`}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRunScheduler(scheduler.scheduler_id)}
                            disabled={runningScheduler === scheduler.scheduler_id || !scheduler.enabled}
                            className="gap-1"
                            data-testid={`run-scheduler-${scheduler.scheduler_id}`}
                          >
                            {runningScheduler === scheduler.scheduler_id ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Play className="w-3 h-3" />
                            )}
                            Run Now
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          
          {/* Scheduler Info Card */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Settings2 className="w-4 h-4" />
                Scheduler Information
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-gray-600 space-y-2">
              <p><strong>Daily:</strong> Runs every day at the specified time.</p>
              <p><strong>Weekly:</strong> Runs once a week on the specified day and time.</p>
              <p><strong>Monthly:</strong> Runs once a month on the specified day (1-31) and time.</p>
              <p className="text-amber-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                All times are in UTC timezone. Disabled tasks will not run automatically.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Rules Tab */}
        <TabsContent value="rules">
          {/* Screenshot Auto-Deletion Policy */}
          <Card className="mb-6">
            <CardHeader className="border-b bg-gradient-to-r from-rose-50 to-pink-50">
              <CardTitle className="flex items-center gap-2 text-rose-800">
                <Camera className="w-5 h-5" />
                Screenshot Auto-Deletion Policy
              </CardTitle>
              <CardDescription>
                Set a global retention period. Screenshots older than this many days will be automatically deleted from all company accounts daily. Set to 0 to keep forever.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSaveScreenshotRetention} className="flex items-end gap-4 flex-wrap">
                <div className="space-y-2">
                  <Label htmlFor="screenshot-retention-days" className="font-semibold">Retention Period</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      id="screenshot-retention-days"
                      type="number"
                      min="0"
                      max="3650"
                      value={screenshotRetentionDays}
                      onChange={(e) => setScreenshotRetentionDays(parseInt(e.target.value) || 0)}
                      className="w-28 text-center text-lg font-medium"
                      data-testid="screenshot-retention-days-input"
                    />
                    <span className="text-gray-600 font-medium">days</span>
                    {screenshotRetentionDays > 0 ? (
                      <span className="text-xs text-rose-700 font-medium bg-rose-100 border border-rose-200 px-3 py-1 rounded-full">
                        Auto-delete after {screenshotRetentionDays} day{screenshotRetentionDays !== 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500 font-medium bg-gray-100 border border-gray-200 px-3 py-1 rounded-full">
                        Disabled — screenshots kept forever
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={savingScreenshotRetention}
                  className="bg-rose-600 hover:bg-rose-700"
                  data-testid="save-screenshot-retention-btn"
                >
                  {savingScreenshotRetention ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Policy
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-b bg-gray-50">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ScrollText className="w-5 h-5 text-indigo-600" />
                    Admin Rules
                  </CardTitle>
                  <CardDescription>Define rules that affect admin accounts</CardDescription>
                </div>
                <Button 
                  onClick={() => openRuleDialog()}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="add-rule-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Rule
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Rule Name</TableHead>
                    <TableHead className="font-semibold">Rules Action</TableHead>
                    <TableHead className="text-right font-semibold">Edit Rule</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-gray-500 py-8">
                        No rules configured. Click &quot;Add Rule&quot; to create one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rules.map((rule) => (
                      <TableRow key={rule.rule_id} data-testid={`rule-row-${rule.rule_id}`}>
                        <TableCell className="font-medium">{rule.rule_name}</TableCell>
                        <TableCell>
                          <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                            {rule.rule_action}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openRuleDialog(rule)}
                              className="text-gray-600 hover:text-gray-700"
                              data-testid={`edit-rule-${rule.rule_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteRule(rule.rule_id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-rule-${rule.rule_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Postman Tab */}
        <TabsContent value="postman">
          <Card>
            <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-amber-50">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileJson className="w-5 h-5 text-orange-600" />
                    Desktop App API Collection
                  </CardTitle>
                  <CardDescription>
                    Postman collection for integrating with Flutter desktop application
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Monitor className="w-8 h-8 text-orange-600" />
                  <Smartphone className="w-6 h-6 text-orange-400" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {/* API Overview */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">API Overview</h3>
                <p className="text-gray-600 mb-4">
                  This Postman collection contains all the APIs required for building a desktop time-tracking application. 
                  Import this collection into Postman to test and integrate with your Flutter desktop app.
                </p>
                
                {/* API Features Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <LogIn className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-blue-900">Authentication</span>
                    </div>
                    <p className="text-sm text-blue-700">Staff login with email/password</p>
                  </div>
                  
                  <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckSquare className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-900">Check-In/Out</span>
                    </div>
                    <p className="text-sm text-green-700">Timezone-aware attendance tracking</p>
                  </div>
                  
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Coffee className="w-5 h-5 text-purple-600" />
                      <span className="font-medium text-purple-900">Break Management</span>
                    </div>
                    <p className="text-sm text-purple-700">Manual & force breaks</p>
                  </div>
                  
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-orange-600" />
                      <span className="font-medium text-orange-900">Idle Detection</span>
                    </div>
                    <p className="text-sm text-orange-700">Auto force-break on idle timeout</p>
                  </div>
                  
                  <div className="p-4 bg-pink-50 rounded-lg border border-pink-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Camera className="w-5 h-5 text-pink-600" />
                      <span className="font-medium text-pink-900">Screenshot Capture</span>
                    </div>
                    <p className="text-sm text-pink-700">Upload periodic screenshots</p>
                  </div>
                  
                  <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-100">
                    <div className="flex items-center gap-2 mb-2">
                      <LogOut className="w-5 h-5 text-cyan-600" />
                      <span className="font-medium text-cyan-900">{t("nav.reports")}</span>
                    </div>
                    <p className="text-sm text-cyan-700">Attendance history & summary</p>
                  </div>
                  
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-5 h-5 text-emerald-600" />
                      <span className="font-medium text-emerald-900">GPS Tracking</span>
                    </div>
                    <p className="text-sm text-emerald-700">Real-time location tracking</p>
                  </div>
                  
                  <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Bell className="w-5 h-5 text-indigo-600" />
                      <span className="font-medium text-indigo-900">{t("nav.notifications")}</span>
                    </div>
                    <p className="text-sm text-indigo-700">Real-time alerts via keep-alive</p>
                  </div>
                  
                  <div className="p-4 bg-rose-50 rounded-lg border border-rose-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className="w-5 h-5 text-rose-600" />
                      <span className="font-medium text-rose-900">Keep-Alive</span>
                    </div>
                    <p className="text-sm text-rose-700">Heartbeat with notification count</p>
                  </div>
                </div>
              </div>
              
              {/* API Endpoints List */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Included Endpoints</h3>
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <div className="space-y-2 font-mono text-sm">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs">POST</span>
                      <span className="text-gray-700">/api/auth/login</span>
                      <span className="text-gray-400 text-xs ml-auto">Staff Authentication</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-blue-500 text-white rounded text-xs">GET</span>
                      <span className="text-gray-700">/api/desktop/staff/info</span>
                      <span className="text-gray-400 text-xs ml-auto">Get Staff Settings</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs">POST</span>
                      <span className="text-gray-700">/api/staff/check-in</span>
                      <span className="text-gray-400 text-xs ml-auto">{t("attendance.check_in")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs">POST</span>
                      <span className="text-gray-700">/api/staff/check-out</span>
                      <span className="text-gray-400 text-xs ml-auto">{t("attendance.check_out")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs">POST</span>
                      <span className="text-gray-700">/api/staff/break/start</span>
                      <span className="text-gray-400 text-xs ml-auto">Start Break</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs">POST</span>
                      <span className="text-gray-700">/api/staff/break/end</span>
                      <span className="text-gray-400 text-xs ml-auto">End Break</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs">POST</span>
                      <span className="text-gray-700">/api/desktop/idle/report</span>
                      <span className="text-gray-400 text-xs ml-auto">Report Idle Time</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs">POST</span>
                      <span className="text-gray-700">/api/desktop/screenshot/upload</span>
                      <span className="text-gray-400 text-xs ml-auto">Upload Screenshot</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs">POST</span>
                      <span className="text-gray-700">/api/desktop/gps/report</span>
                      <span className="text-gray-400 text-xs ml-auto">Report GPS Location</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-blue-500 text-white rounded text-xs">GET</span>
                      <span className="text-gray-700">/api/desktop/gps/history</span>
                      <span className="text-gray-400 text-xs ml-auto">GPS History</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-blue-500 text-white rounded text-xs">GET</span>
                      <span className="text-gray-700">/api/desktop/attendance/status</span>
                      <span className="text-gray-400 text-xs ml-auto">Attendance Status</span>
                    </div>
                    <div className="flex items-center gap-2 pt-2 mt-2 border-t border-gray-200">
                      <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs">POST</span>
                      <span className="text-gray-700">/api/staff/keep-alive</span>
                      <span className="text-gray-400 text-xs ml-auto">Keep-Alive + Notifications</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-blue-500 text-white rounded text-xs">GET</span>
                      <span className="text-gray-700">/api/staff/notifications</span>
                      <span className="text-gray-400 text-xs ml-auto">Get Notifications</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-blue-500 text-white rounded text-xs">GET</span>
                      <span className="text-gray-700">/api/staff/notifications/unread-count</span>
                      <span className="text-gray-400 text-xs ml-auto">Unread Count</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs">POST</span>
                      <span className="text-gray-700">/api/staff/notifications/{'{id}'}/mark-read</span>
                      <span className="text-gray-400 text-xs ml-auto">Mark as Read</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs">POST</span>
                      <span className="text-gray-700">/api/staff/notifications/mark-all-read</span>
                      <span className="text-gray-400 text-xs ml-auto">Mark All Read</span>
                    </div>
                  </div>
                </div>
                
                {/* Keep-Alive Response Info */}
                <div className="mt-4 p-4 bg-rose-50 rounded-lg border border-rose-200">
                  <h4 className="font-medium text-rose-900 mb-2 flex items-center gap-2">
                    <Heart className="w-4 h-4" />
                    Keep-Alive Response (includes notification counts)
                  </h4>
                  <pre className="text-xs text-rose-800 bg-white p-3 rounded border overflow-x-auto">
{`{
  "status": "ok",
  "message": "Keep-alive received",
  "last_active": "2026-02-01T12:00:00Z",
  "device_type": "desktop",
  "current_status": "working",
  "attendance_id": "att_xxx",
  "notifications": {
    "unread_count": 3,
    "leave_unread_count": 1
  }
}`}
                  </pre>
                </div>
              </div>
              
              {/* Admin Notification Endpoints */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Notification Endpoints</h3>
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <div className="space-y-2 font-mono text-sm">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs">POST</span>
                      <span className="text-gray-700">/api/admin/notifications/send</span>
                      <span className="text-gray-400 text-xs ml-auto">Send to Team/Staff</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-blue-500 text-white rounded text-xs">GET</span>
                      <span className="text-gray-700">/api/admin/notifications</span>
                      <span className="text-gray-400 text-xs ml-auto">List Sent Notifications</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-blue-500 text-white rounded text-xs">GET</span>
                      <span className="text-gray-700">/api/admin/notifications/{'{id}'}/recipients</span>
                      <span className="text-gray-400 text-xs ml-auto">Recipient Read Status</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs">POST</span>
                      <span className="text-gray-700">/api/admin/notifications/upload-attachment</span>
                      <span className="text-gray-400 text-xs ml-auto">Upload Attachment</span>
                    </div>
                  </div>
                </div>
                
                {/* Send Notification Request Info */}
                <div className="mt-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <h4 className="font-medium text-indigo-900 mb-2 flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    Send Notification Request Body
                  </h4>
                  <pre className="text-xs text-indigo-800 bg-white p-3 rounded border overflow-x-auto">
{`{
  "recipient_type": "team" | "staff",
  "team_id": "team_xxx",       // Required if type is "team"
  "staff_id": "staff_xxx",     // Required if type is "staff"
  "message": "Your message here",
  "attachment_url": "/uploads/notifications/file.pdf"  // Optional
}`}
                  </pre>
                </div>
              </div>
              
              {/* Download Section */}
              <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold mb-2">Download Postman Collection</h3>
                    <p className="text-orange-100 text-sm">
                      Import this JSON file into Postman to get started with API testing
                    </p>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        const response = await axios.get(`${API}/desktop/postman-collection`, { withCredentials: true });
                        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'Trackly_Desktop_API.postman_collection.json';
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                        showMessage('success', 'Postman collection downloaded successfully!');
                      } catch (error) {
                        showMessage('error', 'Failed to download collection');
                      }
                    }}
                    className="bg-white text-orange-600 hover:bg-orange-50 px-6 py-3 h-auto"
                    data-testid="download-postman-btn"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download Collection
                  </Button>
                </div>
              </div>
              
              {/* Instructions */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-semibold text-gray-900 mb-2">How to use:</h4>
                <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                  <li>Download the Postman collection using the button above</li>
                  <li>Open Postman and click &quot;Import&quot; → &quot;Upload Files&quot;</li>
                  <li>Select the downloaded JSON file</li>
                  <li>Update the <code className="bg-gray-200 px-1 rounded">base_url</code> variable with your API URL</li>
                  <li>Run the &quot;Staff Login&quot; request to get your session token</li>
                  <li>The token will be automatically saved for other requests</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Chat Widget Tab */}
        <TabsContent value="chat-widget">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-indigo-600" />
                Chat Widget
              </CardTitle>
              <CardDescription>
                Configure a global third-party chat widget for all public pages (Landing, Terms, Privacy).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveChatWidget} className="space-y-6">
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-1">
                    <p className="font-medium">Enable Chat Widget</p>
                    <p className="text-sm text-gray-600">If enabled, the widget snippet will be injected on public pages.</p>
                  </div>
                  <Switch checked={chatWidgetEnabled} onCheckedChange={setChatWidgetEnabled} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="chat-widget-snippet">Widget Snippet</Label>
                  <textarea
                    id="chat-widget-snippet"
                    value={chatWidgetSnippet}
                    onChange={(e) => setChatWidgetSnippet(e.target.value)}
                    placeholder="Paste your full widget snippet here (script tags allowed)"
                    className="w-full min-h-[220px] rounded-md border border-gray-300 p-3 text-sm font-mono"
                    data-testid="chat-widget-snippet"
                  />
                  <p className="text-sm text-gray-600">
                    Tip: Use this for providers like Tawk.to, Crisp, Intercom, etc.
                  </p>
                </div>

                <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Chat Widget'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Language Tab */}
        <TabsContent value="language" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-600" />
                {t('settings.language_settings')}
              </CardTitle>
              <CardDescription>{t('settings.select_language')}</CardDescription>
            </CardHeader>
            <CardContent>
              <LanguageSelector showLabel className="max-w-md" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>Choose light, dark, or match your system theme</CardDescription>
            </CardHeader>
            <CardContent>
              <ThemeToggle />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <Dialog open={ruleDialogOpen} onOpenChange={setRuleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Rule' : 'Add Rule'}</DialogTitle>
            <DialogDescription>
              {editingRule ? 'Update the rule settings' : 'Create a new admin rule'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveRule} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rule-name">Rule Name</Label>
              <Input
                id="rule-name"
                value={ruleName}
                onChange={(e) => setRuleName(e.target.value)}
                placeholder="e.g., Free Staff"
                required
                data-testid="rule-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rule-action">Rules Action</Label>
              <Input
                id="rule-action"
                value={ruleAction}
                onChange={(e) => setRuleAction(e.target.value)}
                placeholder="e.g., 2 Staff"
                required
                data-testid="rule-action-input"
              />
              <p className="text-xs text-gray-500">
                For &quot;Free Staff&quot; rule, enter the number of free staff allowed (e.g., &quot;2 Staff&quot;)
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRuleDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                {saving ? 'Saving...' : (editingRule ? 'Update Rule' : 'Create Rule')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


    </div>
  );
}

export default SettingsPage;
